import type { Place } from '../types';
import { CERT_STATE, certificateProgress, formatDate, formatSince } from '../lib/trust';
import { useT } from '../i18n';
import './CertificateStrip.css';

/**
 * The certificate strip — the one element this app should be remembered by.
 *
 * Every other halal app shows a flat "Verified" badge. The whole point here is
 * that a certificate is a document with an issuer, a reference number and a
 * death date, so this renders it as a credential rather than a sticker: issuer
 * in letterspaced caps, reference in mono, and a validity bar that drains as the
 * certificate ages so remaining life is legible at a glance.
 *
 * Brass is used here and nowhere else in the app, so the eye learns that brass
 * means "an actual credential", not a marketing flourish.
 *
 * Registers that publish no expiry (most French certifiers, which revoke by
 * deleting the entry) get an open-ended dashed rail instead of a draining bar —
 * drawing a full bar for them would invent a guarantee they never made.
 */
export function CertificateStrip({ place }: { place: Place }) {
    const { t, lang } = useT();
    const halal = place.halal;
    const state = CERT_STATE[place.certState];
    if (!halal || !state.showsCertificate || !state.label) return null;

    const progress = certificateProgress(halal);
    const openEnded = halal.validityModel === 'delisting';
    const dead = place.certState === 'expired' || place.certState === 'delisted';
    const confirmedWhen = formatSince(place.liveCheckedAt, t, lang.code)
        || (halal.listedAsOf ? formatDate(halal.listedAsOf, lang.code) : '—');

    return (
        <section className={`cert tone-${state.tone}`} aria-label={t('cert.label')}>
            <header className="cert__head">
                <span className="cert__seal" aria-hidden="true" />
                <span className="cert__issuer">{halal.certifier?.name ?? t('cert.noCertifier')}</span>
            </header>

            <p className="cert__state">{t(state.label)}</p>

            {/* Validity rail. Open-ended registers get a dashed, unterminated
                rail; dated ones get a bar that drains toward the expiry date. */}
            {openEnded ? (
                <div className="cert__rail cert__rail--open" role="img"
                     aria-label={t('cert.ariaOpen', { when: confirmedWhen })}>
                    <span className="cert__rail-dot" />
                    <span className="cert__rail-line" />
                    <span className="cert__rail-open">{t('cert.noEndDate')}</span>
                </div>
            ) : progress !== null ? (
                <div className="cert__rail" role="img"
                     aria-label={dead
                         ? t('cert.ariaExpired', { date: formatDate(halal.validUntil, lang.code) })
                         : t('cert.ariaValid', { date: formatDate(halal.validUntil, lang.code) })}>
                    <span className="cert__rail-track">
                        <span className="cert__rail-fill" style={{ inlineSize: `${Math.round(progress * 100)}%` }} />
                    </span>
                </div>
            ) : null}

            <dl className="cert__meta">
                {halal.certificateId && (
                    <div className="cert__field">
                        <dt>{t('cert.reference')}</dt>
                        <dd className="cert__mono">{halal.certificateId}</dd>
                    </div>
                )}
                {halal.certificateType && !halal.certificateId && (
                    <div className="cert__field">
                        <dt>{t('cert.category')}</dt>
                        <dd className="cert__mono">{halal.certificateType}</dd>
                    </div>
                )}
                <div className="cert__field">
                    <dt>{openEnded ? t('cert.confirmed') : dead ? t('cert.expiredOn') : t('cert.validUntil')}</dt>
                    <dd className="cert__mono">
                        {openEnded ? confirmedWhen : (formatDate(halal.validUntil, lang.code) || '—')}
                    </dd>
                </div>
            </dl>

            {place.certState === 'expired' && <p className="cert__note">{t('cert.expiredNote')}</p>}
            {place.certState === 'delisted' && (
                <p className="cert__note">{t('cert.delistedNote', { certifier: halal.certifier?.name ?? '' })}</p>
            )}
            {place.certState === 'renewal_pending' && <p className="cert__note">{t('cert.pendingNote')}</p>}
            {openEnded && <p className="cert__note cert__note--quiet">{t('cert.openEndedNote')}</p>}
        </section>
    );
}
